import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { officeAPI } from '../../services/api';
import { format } from 'date-fns';
import { PARCEL_TYPES } from '../../constants/parcelTypes';

const fmtDate = (d, fmt = 'yyyy/MM/dd HH:mm') => (d ? format(new Date(d), fmt) : '—');

const STATUS_LABELS = {
  draft: { label: 'مسودة', color: 'bg-gray-100 text-gray-600' },
  payment_pending: { label: 'بانتظار الدفع', color: 'bg-amber-100 text-amber-700' },
  payment_submitted: { label: 'إيصال قيد المراجعة', color: 'bg-amber-100 text-amber-700' },
  waiting_delivery: { label: 'بانتظار التسليم', color: 'bg-amber-100 text-amber-700' },
  received_by_office: { label: 'مستلم بالمكتب', color: 'bg-blue-100 text-blue-700' },
  waiting_transportation: { label: 'بانتظار رحلة', color: 'bg-gray-100 text-gray-600' },
  loaded: { label: 'محمّل على الباص', color: 'bg-indigo-100 text-indigo-700' },
  in_transit: { label: 'في الطريق', color: 'bg-purple-100 text-purple-700' },
  arrived: { label: 'وصل', color: 'bg-teal-100 text-teal-700' },
  ready_for_collection: { label: 'جاهز للاستلام', color: 'bg-emerald-100 text-emerald-700' },
  completed: { label: 'تم التسليم', color: 'bg-emerald-100 text-emerald-700' },
  returned: { label: 'مُعاد', color: 'bg-gray-200 text-gray-700' },
  cancelled: { label: 'ملغى', color: 'bg-gray-200 text-gray-700' },
  damaged: { label: 'تلف', color: 'bg-red-100 text-red-700' },
  lost: { label: 'مفقود', color: 'bg-red-100 text-red-700' },
  dropoff_deadline_expired: { label: 'انتهى موعد التسليم', color: 'bg-red-100 text-red-700' },
};

// Past the 30-min pre-departure cutoff and still not received — normal
// "استلام وتحقق" is blocked server-side until an admin/supervisor grants a
// late exception (parcelStatusService.approveLateException). Covers both the
// already-flipped dropoff_deadline_expired status and the ~60s window where
// the cutoff has passed but the expiry cron hasn't run yet.
const isPastDropoffCutoff = (p) =>
  !p.lateExceptionApprovedBy && (p.status === 'dropoff_deadline_expired' ||
    (p.status === 'waiting_delivery' && p.dropoffDeadline && new Date(p.dropoffDeadline) < new Date()));

// Mirrors the two tiers in officeParcelController.js. Contact/descriptive
// fields (names, phones, description) never affect price, so they stay
// editable right up to the parcel closing out. Weight/insurance/declared-
// value/fragile freeze once payment has actually been reviewed against the
// original figures — corrections past that point go through "استلام وتحقق"
// instead, which recalculates price and records the difference explicitly.
const FULLY_EDITABLE_STATUSES = ['draft', 'payment_pending', 'payment_submitted', 'waiting_transportation'];
const CONTACT_ONLY_EDITABLE_STATUSES = ['paid', 'waiting_delivery', 'received_by_office', 'loaded', 'in_transit', 'arrived', 'ready_for_collection'];
const EDITABLE_STATUSES = [...FULLY_EDITABLE_STATUSES, ...CONTACT_ONLY_EDITABLE_STATUSES];

const isFullName = (name) => String(name || '').trim().split(/\s+/).filter(Boolean).length >= 4;

const EditParcelModal = ({ parcel, onClose, onDone }) => {
  const [senderName, setSenderName]   = useState(parcel.senderName || '');
  const [senderPhone, setSenderPhone] = useState(parcel.senderPhone || '');
  const [receiverName, setReceiverName]   = useState(parcel.receiverName || '');
  const [receiverPhone, setReceiverPhone] = useState(parcel.receiverPhone || '');
  const [parcelType, setParcelType]   = useState(parcel.parcelType || 'general_goods');
  const [weightKg, setWeightKg]       = useState(parcel.weightKg || '');
  const [sizeCategory, setSizeCategory] = useState(parcel.sizeCategory || 'medium');
  const [quantity, setQuantity]       = useState(parcel.quantity || 1);
  const [isFragile, setIsFragile]     = useState(!!parcel.isFragile);
  const [isInsured, setIsInsured]     = useState(!!parcel.isInsured);
  const [declaredValue, setDeclaredValue] = useState(parcel.declaredValue || '');
  const [specialInstructions, setSpecialInstructions] = useState(parcel.specialInstructions || '');
  const [saving, setSaving] = useState(false);

  // Once payment's been reviewed against the original weight/insurance
  // figures, those freeze here — correcting them goes through "استلام
  // وتحقق" instead. Contact/descriptive info stays editable regardless.
  const pricingLocked = CONTACT_ONLY_EDITABLE_STATUSES.includes(parcel.status);

  const nameValid = isFullName(senderName) && isFullName(receiverName);

  const submit = async () => {
    if (!nameValid) return toast.error('الاسم الرباعي مطلوب للمرسل والمستلم');
    if (!pricingLocked && (!weightKg || Number(weightKg) <= 0)) return toast.error('وزن غير صحيح');
    setSaving(true);
    try {
      const payload = {
        senderName, senderPhone, receiverName, receiverPhone,
        parcelType, sizeCategory, specialInstructions,
      };
      if (!pricingLocked) {
        Object.assign(payload, { weightKg, quantity, isFragile, isInsured, declaredValue });
      }
      await officeAPI.updateParcel(parcel.id, payload);
      toast.success('تم تحديث بيانات الطرد');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تحديث الطرد');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="bg-white rounded-2xl p-6 w-full max-w-lg max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-lg mb-1">تعديل بيانات الطرد</h3>
        <p className="text-xs text-gray-500 mb-4">#{parcel.trackingNumber}</p>
        {pricingLocked && (
          <div className="bg-amber-50 border border-amber-200 text-amber-800 text-xs rounded-xl px-3 py-2 mb-4">
            تم تأكيد الدفع لهذا الطرد — يمكن تعديل بيانات الاتصال والوصف فقط. لتصحيح الوزن استخدم "استلام وتحقق".
          </div>
        )}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">اسم المرسل الرباعي</label>
            <input value={senderName} onChange={(e) => setSenderName(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">هاتف المرسل</label>
            <input dir="ltr" value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">اسم المستلم الرباعي</label>
            <input value={receiverName} onChange={(e) => setReceiverName(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">هاتف المستلم</label>
            <input dir="ltr" value={receiverPhone} onChange={(e) => setReceiverPhone(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">نوع الطرد</label>
            <select value={parcelType} onChange={(e) => setParcelType(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm">
              {PARCEL_TYPES.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">الحجم</label>
            <select value={sizeCategory} onChange={(e) => setSizeCategory(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm">
              <option value="small">صغير</option><option value="medium">متوسط</option><option value="large">كبير</option>
            </select>
          </div>
          {!pricingLocked && (
            <>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">الوزن (كجم)</label>
                <input type="number" step="0.1" value={weightKg} onChange={(e) => setWeightKg(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
              </div>
              <div>
                <label className="block text-xs font-bold text-gray-600 mb-1">الكمية</label>
                <input type="number" min="1" value={quantity} onChange={(e) => setQuantity(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
              </div>
            </>
          )}
        </div>
        {!pricingLocked && (
          <>
            <div className="flex items-center gap-4 mt-3">
              <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                <input type="checkbox" checked={isFragile} onChange={(e) => setIsFragile(e.target.checked)} /> قابل للكسر
              </label>
              <label className="flex items-center gap-1.5 text-xs font-bold text-gray-600">
                <input type="checkbox" checked={isInsured} onChange={(e) => setIsInsured(e.target.checked)} /> مؤمَّن
              </label>
            </div>
            {isInsured && (
              <div className="mt-3">
                <label className="block text-xs font-bold text-gray-600 mb-1">القيمة المصرح بها</label>
                <input type="number" value={declaredValue} onChange={(e) => setDeclaredValue(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
              </div>
            )}
          </>
        )}
        <div className="mt-3">
          <label className="block text-xs font-bold text-gray-600 mb-1">تعليمات خاصة</label>
          <textarea rows={2} value={specialInstructions} onChange={(e) => setSpecialInstructions(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm" />
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={saving} className="flex-1 bg-blue-700 hover:bg-blue-800 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
            {saving ? '...جارٍ الحفظ' : 'حفظ التعديلات'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

const VerifyModal = ({ parcel, onClose, onDone }) => {
  const [weight, setWeight] = useState(parcel.weightKg || '');
  const [size, setSize] = useState(parcel.sizeCategory || '');
  const [type, setType] = useState(parcel.parcelType || '');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    setSaving(true);
    try {
      const res = await officeAPI.verifyAndReceiveParcel(parcel.id, {
        verifiedWeightKg: weight, verifiedSizeCategory: size, verifiedParcelType: type,
      });
      const extra = res.data.data?.additionalAmountDue;
      toast.success(extra > 0 ? `تم الاستلام — مبلغ إضافي مستحق: ${extra} ج.س` : 'تم استلام الطرد والتحقق منه');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في التحقق من الطرد');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h3 className="font-bold text-lg mb-1">استلام والتحقق من الطرد</h3>
        <p className="text-xs text-gray-500 mb-4">#{parcel.trackingNumber} — قارن الوزن/الحجم الفعلي بما تم تصريحه ({parcel.weightKg} كجم)</p>
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">الوزن الفعلي (كجم)</label>
            <input type="number" step="0.1" value={weight} onChange={(e) => setWeight(e.target.value)}
              className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400" />
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">الحجم</label>
            <select value={size} onChange={(e) => setSize(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm">
              <option value="small">صغير</option>
              <option value="medium">متوسط</option>
              <option value="large">كبير</option>
            </select>
          </div>
          <div>
            <label className="block text-xs font-bold text-gray-600 mb-1">نوع الطرد</label>
            <select value={type} onChange={(e) => setType(e.target.value)} className="w-full border rounded-xl px-3 py-2 text-sm">
              {PARCEL_TYPES.map((t) => (
                <option key={t.key} value={t.key}>{t.label}</option>
              ))}
            </select>
          </div>
        </div>
        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={saving} className="flex-1 bg-blue-700 hover:bg-blue-800 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
            {saving ? '...جارٍ الحفظ' : 'تأكيد الاستلام'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

const RejectModal = ({ parcel, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error('سبب الرفض مطلوب');
    setSaving(true);
    try {
      await officeAPI.rejectParcelAtDropoff(parcel.id, { reason: reason.trim() });
      toast.success('تم رفض استلام الطرد');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في رفض الطرد');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h3 className="font-bold text-lg mb-1">رفض استلام الطرد</h3>
        <p className="text-xs text-gray-500 mb-4">#{parcel.trackingNumber} — سيُعاد الطرد للمرسل ولن يُشحن</p>
        <label className="block text-xs font-bold text-gray-600 mb-1">السبب *</label>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: محتوى ممنوع / تعبئة غير مناسبة / وزن غير مطابق بشكل كبير"
          className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400" />
        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
            {saving ? '...جارٍ الحفظ' : 'تأكيد الرفض'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

const LateExceptionModal = ({ parcel, onClose, onDone }) => {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!reason.trim()) return toast.error('سبب التجاوز الاستثنائي مطلوب');
    setSaving(true);
    try {
      await officeAPI.approveParcelLateException(parcel.id, { reason: reason.trim() });
      toast.success('تم اعتماد التجاوز الاستثنائي — يمكنك الآن استلام الطرد');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في اعتماد التجاوز');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md">
        <h3 className="font-bold text-lg mb-1">تجاوز استثنائي لموعد التسليم</h3>
        <p className="text-xs text-gray-500 mb-4">#{parcel.trackingNumber} — انتهى موعد تسليم الطرد (30 دقيقة قبل الانطلاق). هذا الإجراء يتطلب صلاحية مدير/مشرف مكتب.</p>
        <label className="block text-xs font-bold text-gray-600 mb-1">سبب التجاوز *</label>
        <textarea rows={3} value={reason} onChange={(e) => setReason(e.target.value)}
          placeholder="مثال: تأخر الراكب في الوصول بسبب ازدحام، تمت الموافقة هاتفياً..."
          className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-amber-400" />
        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={saving} className="flex-1 bg-amber-600 hover:bg-amber-700 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
            {saving ? '...جارٍ الحفظ' : 'اعتماد التجاوز'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

const CollectModal = ({ parcel, onClose, onDone }) => {
  const [otp, setOtp] = useState('');
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!otp.trim()) return toast.error('رمز الاستلام مطلوب');
    setSaving(true);
    try {
      await officeAPI.markParcelCollected(parcel.id, otp.trim());
      toast.success('تم تسليم الطرد للمستلم');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'رمز الاستلام غير صحيح');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="bg-white rounded-2xl p-6 w-full max-w-sm">
        <h3 className="font-bold text-lg mb-1">تسليم الطرد للمستلم</h3>
        <p className="text-xs text-gray-500 mb-4">#{parcel.trackingNumber} — اطلب من المستلم رمز الاستلام المرسل له عبر واتساب</p>
        <label className="block text-xs font-bold text-gray-600 mb-1">رمز الاستلام (٦ أرقام)</label>
        <input dir="ltr" inputMode="numeric" maxLength={6} value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, ''))}
          placeholder="123456"
          className="w-full border rounded-xl px-3 py-2.5 text-center text-lg tracking-[0.3em] font-mono focus:outline-none focus:ring-2 focus:ring-blue-400" />
        <div className="flex gap-2 mt-5">
          <button onClick={submit} disabled={saving || otp.length !== 6} className="flex-1 bg-blue-700 hover:bg-blue-800 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
            {saving ? '...جارٍ التحقق' : 'تأكيد التسليم'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

const INCIDENT_TYPES = [
  { key: 'damaged', label: 'تلف', icon: '💥' },
  { key: 'lost', label: 'مفقود', icon: '❓' },
  { key: 'delayed', label: 'تأخير', icon: '⏱️' },
  { key: 'other', label: 'أخرى', icon: '📝' },
];

const IncidentModal = ({ parcel, onClose, onDone }) => {
  const [type, setType] = useState('damaged');
  const [description, setDescription] = useState('');
  const [photos, setPhotos] = useState([]);
  const [saving, setSaving] = useState(false);

  const submit = async () => {
    if (!description.trim()) return toast.error('وصف الحادثة مطلوب');
    setSaving(true);
    try {
      const fd = new FormData();
      fd.append('type', type);
      fd.append('description', description.trim());
      photos.forEach((f) => fd.append('photos', f));
      await officeAPI.reportParcelIncident(parcel.id, fd);
      toast.success('تم تسجيل الحادثة');
      onDone();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تسجيل الحادثة');
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4" dir="rtl">
      <div className="bg-white rounded-2xl p-6 w-full max-w-md max-h-[90vh] overflow-y-auto">
        <h3 className="font-bold text-lg mb-1">الإبلاغ عن مشكلة</h3>
        <p className="text-xs text-gray-500 mb-4">#{parcel.trackingNumber}</p>
        <div className="flex gap-2 flex-wrap mb-3">
          {INCIDENT_TYPES.map((t) => (
            <button key={t.key} type="button" onClick={() => setType(t.key)}
              className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full border text-xs font-semibold transition-all ${
                type === t.key ? 'bg-red-600 text-white border-red-600' : 'bg-gray-50 text-gray-700 border-gray-300 hover:border-red-400'
              }`}>
              {t.icon} {t.label}
            </button>
          ))}
        </div>
        <label className="block text-xs font-bold text-gray-600 mb-1">الوصف *</label>
        <textarea rows={3} value={description} onChange={(e) => setDescription(e.target.value)}
          placeholder="اشرح ما حدث بالتفصيل"
          className="w-full border rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-red-400 mb-3" />
        <label className="block text-xs font-bold text-gray-600 mb-1">صور (اختياري، حتى ٣)</label>
        <input type="file" accept="image/jpeg,image/png" multiple
          onChange={(e) => setPhotos(Array.from(e.target.files || []).slice(0, 3))}
          className="text-xs mb-1" />
        {photos.length > 0 && <p className="text-xs text-gray-500 mb-3">{photos.length} صورة مختارة</p>}
        <div className="flex gap-2 mt-4">
          <button onClick={submit} disabled={saving} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-bold disabled:opacity-60">
            {saving ? '...جارٍ الحفظ' : 'تسجيل الحادثة'}
          </button>
          <button onClick={onClose} className="px-4 py-2.5 rounded-xl border text-sm font-semibold text-gray-600">إلغاء</button>
        </div>
      </div>
    </div>
  );
};

export default function OfficeParcelsPage() {
  const navigate = useNavigate();
  const [scope, setScope] = useState('departing');
  const [parcels, setParcels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [verifying, setVerifying] = useState(null);
  const [editing, setEditing] = useState(null);
  const [rejecting, setRejecting] = useState(null);
  const [grantingException, setGrantingException] = useState(null);
  const [collecting, setCollecting] = useState(null);
  const [reportingIncident, setReportingIncident] = useState(null);
  const [lookupNum, setLookupNum] = useState('');

  const fetchParcels = useCallback(async () => {
    setLoading(true);
    try {
      const res = await officeAPI.getOfficeParcels(scope);
      setParcels(res.data.data || []);
    } catch { toast.error('خطأ في تحميل الطرود'); }
    finally { setLoading(false); }
  }, [scope]);

  useEffect(() => { fetchParcels(); }, [fetchParcels]);

  const doAction = async (fn, successMsg) => {
    try {
      await fn();
      toast.success(successMsg);
      fetchParcels();
    } catch (err) {
      toast.error(err.response?.data?.message || 'خطأ في تنفيذ الإجراء');
    }
  };

  const collectExtra = (p) => doAction(
    () => officeAPI.collectAdditionalPayment(p.id, { note: 'تم التحصيل عند الاستلام' }),
    'تم تسجيل استلام المبلغ الإضافي'
  );

  const printLabel = async (p) => {
    try {
      const res = await officeAPI.getParcelLabel(p.id);
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      toast.error(err.response?.data?.message || 'تعذّر إنشاء الملصق');
    }
  };

  const handleLookup = async (e) => {
    e.preventDefault();
    if (!lookupNum.trim()) return;
    try {
      const res = await officeAPI.lookupParcel({ trackingNumber: lookupNum.trim() });
      const p = res.data.data;
      if (p.departureOfficeId === parcels[0]?.departureOfficeId || true) {
        setParcels([p]);
        toast.info(`تم العثور على الطرد — الحالة: ${STATUS_LABELS[p.status]?.label || p.status}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.message || 'رقم التتبع غير موجود');
    }
  };

  const CLOSED_STATUSES = ['completed', 'cancelled', 'returned', 'rejected', 'damaged', 'lost'];

  const renderActions = (p) => {
    const actions = [];
    if (scope === 'departing') {
      if (EDITABLE_STATUSES.includes(p.status)) {
        actions.push(<button key="edit" onClick={() => setEditing(p)} className="bg-gray-700 hover:bg-gray-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg">تعديل</button>);
      }
      if (isPastDropoffCutoff(p)) {
        actions.push(<button key="late-exception" onClick={() => setGrantingException(p)} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg">⏰ طلب تجاوز استثنائي</button>);
        actions.push(<button key="reject" onClick={() => setRejecting(p)} className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-bold px-3 py-1.5 rounded-lg">رفض الاستلام</button>);
      } else if (['waiting_delivery', 'paid'].includes(p.status)) {
        actions.push(<button key="verify" onClick={() => setVerifying(p)} className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg">استلام وتحقق</button>);
        actions.push(<button key="reject" onClick={() => setRejecting(p)} className="bg-red-50 hover:bg-red-100 text-red-700 border border-red-200 text-xs font-bold px-3 py-1.5 rounded-lg">رفض الاستلام</button>);
      }
      if (p.status === 'received_by_office' || p.status === 'waiting_transportation') {
        if (parseFloat(p.additionalAmountDue) > 0 && !p.additionalPaymentCollected) {
          actions.push(<button key="extra" onClick={() => collectExtra(p)} className="bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold px-3 py-1.5 rounded-lg">تحصيل {p.additionalAmountDue} ج.س</button>);
        }
        if (p.tripId) {
          actions.push(<button key="loaded" onClick={() => doAction(() => officeAPI.markParcelLoaded(p.id), 'تم تحميل الطرد')} className="bg-indigo-600 hover:bg-indigo-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg">تحميل على الباص</button>);
        }
      }
      if (p.status === 'loaded') {
        actions.push(<button key="transit" onClick={() => doAction(() => officeAPI.markParcelInTransit(p.id), 'تم تأكيد المغادرة')} className="bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg">تأكيد المغادرة</button>);
      }
      if (['received_by_office', 'waiting_transportation', 'loaded'].includes(p.status)) {
        actions.push(<button key="label" onClick={() => printLabel(p)} className="bg-gray-100 hover:bg-gray-200 text-gray-700 border border-gray-300 text-xs font-bold px-3 py-1.5 rounded-lg">🏷️ طباعة الملصق</button>);
      }
    } else {
      if (p.status === 'in_transit') {
        actions.push(<button key="arrived" onClick={() => doAction(() => officeAPI.markParcelArrived(p.id), 'تم تأكيد الوصول')} className="bg-teal-600 hover:bg-teal-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg">تأكيد الوصول</button>);
      }
      if (p.status === 'arrived') {
        actions.push(<button key="ready" onClick={() => doAction(() => officeAPI.markParcelReady(p.id), 'تم إرسال رمز الاستلام للمستلم عبر واتساب')} className="bg-emerald-600 hover:bg-emerald-700 text-white text-xs font-bold px-3 py-1.5 rounded-lg">جاهز للاستلام</button>);
      }
      if (p.status === 'ready_for_collection') {
        actions.push(<button key="collect" onClick={() => setCollecting(p)} className="bg-blue-700 hover:bg-blue-800 text-white text-xs font-bold px-3 py-1.5 rounded-lg">تسليم للمستلم</button>);
      }
    }
    if (!CLOSED_STATUSES.includes(p.status)) {
      actions.push(<button key="incident" onClick={() => setReportingIncident(p)} className="bg-white hover:bg-red-50 text-red-600 border border-red-200 text-xs font-bold px-3 py-1.5 rounded-lg">⚠️ مشكلة</button>);
    }
    return actions;
  };

  return (
    <div dir="rtl" className="p-4 md:p-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-5 gap-3">
        <div>
          <h1 className="text-xl font-bold text-gray-900">الطرود</h1>
          <p className="text-xs text-gray-500 mt-0.5">استلام، تحقق، وتسليم طرود المكتب</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <form onSubmit={handleLookup} className="flex gap-2 flex-1 sm:flex-none min-w-0">
            <input value={lookupNum} onChange={(e) => setLookupNum(e.target.value)} placeholder="رقم التتبع..."
              className="border rounded-xl px-3 py-2 text-sm w-full sm:w-44 min-w-0 focus:outline-none focus:ring-2 focus:ring-blue-400" />
            <button type="submit" className="flex-shrink-0 bg-gray-800 hover:bg-gray-900 text-white text-sm font-bold px-4 py-2 rounded-xl">بحث</button>
          </form>
          <button onClick={() => navigate('/office/new-parcel')} className="flex-shrink-0 bg-blue-700 hover:bg-blue-800 text-white text-sm font-bold px-4 py-2 rounded-xl">+ طرد جديد</button>
        </div>
      </div>

      <div className="inline-flex bg-gray-100 rounded-xl p-1 gap-1 mb-4">
        <button onClick={() => setScope('departing')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${scope === 'departing' ? 'bg-blue-700 text-white' : 'text-gray-500'}`}>مغادرة</button>
        <button onClick={() => setScope('arriving')} className={`px-4 py-2 rounded-lg text-sm font-bold transition-all ${scope === 'arriving' ? 'bg-blue-700 text-white' : 'text-gray-500'}`}>وصول</button>
      </div>

      <div className="bg-white rounded-2xl border overflow-hidden">
        {loading ? (
          <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
        ) : parcels.length === 0 ? (
          <div className="py-16 text-center text-gray-400">لا توجد طرود</div>
        ) : (
          <>
            {/* Desktop/tablet table */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">رقم التتبع</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">المرسل / المستلم</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">الرحلة</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">الوزن</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">الحالة</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600">إجراء</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {parcels.map((p) => {
                    const st = STATUS_LABELS[p.status] || { label: p.status, color: 'bg-gray-100 text-gray-600' };
                    return (
                      <tr key={p.id} className="hover:bg-gray-50">
                        <td className="px-4 py-3 font-mono font-bold text-blue-700">{p.trackingNumber}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">{p.senderName} ← {p.receiverName}</td>
                        <td className="px-4 py-3 text-gray-600 text-xs">
                          {p.trip ? <>{p.trip.fromCity} ← {p.trip.toCity}<br /><span className="text-gray-400">{fmtDate(p.trip.departureTime)}</span></> : '—'}
                        </td>
                        <td className="px-4 py-3 text-gray-600">{p.weightKg} كجم</td>
                        <td className="px-4 py-3">
                          <span className={`px-2.5 py-1 rounded-full text-xs font-semibold ${st.color}`}>{st.label}</span>
                          {isPastDropoffCutoff(p) && p.status !== 'dropoff_deadline_expired' && (
                            <span className="block mt-1 text-xs font-bold text-red-700">⏰ انتهى موعد التسليم</span>
                          )}
                        </td>
                        <td className="px-4 py-3"><div className="flex gap-1.5 flex-wrap">{renderActions(p)}</div></td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            {/* Mobile card list — same data/actions as the table above, laid
                out as app-style stacked cards instead of a horizontally
                scrolling table. */}
            <div className="md:hidden divide-y">
              {parcels.map((p) => {
                const st = STATUS_LABELS[p.status] || { label: p.status, color: 'bg-gray-100 text-gray-600' };
                return (
                  <div key={p.id} className="p-4 space-y-2.5">
                    <div className="flex items-start justify-between gap-2">
                      <span className="font-mono font-bold text-blue-700 text-sm">{p.trackingNumber}</span>
                      <div className="flex flex-col items-end gap-1">
                        <span className={`px-2.5 py-1 rounded-full text-xs font-semibold flex-shrink-0 ${st.color}`}>{st.label}</span>
                        {isPastDropoffCutoff(p) && p.status !== 'dropoff_deadline_expired' && (
                          <span className="text-xs font-bold text-red-700">⏰ انتهى موعد التسليم</span>
                        )}
                      </div>
                    </div>
                    <p className="text-xs text-gray-600">{p.senderName} ← {p.receiverName}</p>
                    <div className="flex items-center justify-between text-xs text-gray-500">
                      <span>{p.trip ? <>{p.trip.fromCity} ← {p.trip.toCity}</> : '—'}</span>
                      <span>{p.weightKg} كجم</span>
                    </div>
                    {p.trip && <p className="text-xs text-gray-400">{fmtDate(p.trip.departureTime)}</p>}
                    <div className="flex gap-2 flex-wrap pt-1">{renderActions(p)}</div>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>

      {verifying && (
        <VerifyModal parcel={verifying} onClose={() => setVerifying(null)} onDone={() => { setVerifying(null); fetchParcels(); }} />
      )}
      {editing && (
        <EditParcelModal parcel={editing} onClose={() => setEditing(null)} onDone={() => { setEditing(null); fetchParcels(); }} />
      )}
      {rejecting && (
        <RejectModal parcel={rejecting} onClose={() => setRejecting(null)} onDone={() => { setRejecting(null); fetchParcels(); }} />
      )}
      {grantingException && (
        <LateExceptionModal parcel={grantingException} onClose={() => setGrantingException(null)} onDone={() => { setGrantingException(null); fetchParcels(); }} />
      )}
      {collecting && (
        <CollectModal parcel={collecting} onClose={() => setCollecting(null)} onDone={() => { setCollecting(null); fetchParcels(); }} />
      )}
      {reportingIncident && (
        <IncidentModal parcel={reportingIncident} onClose={() => setReportingIncident(null)} onDone={() => { setReportingIncident(null); fetchParcels(); }} />
      )}
    </div>
  );
}
